import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule, ActivatedRoute } from '@angular/router';
import { AuthService, DevMockUser } from '../../services/auth.service';
import { PosthogService } from '../../services/posthog.service';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule],
  template: `
    <div class="auth-container">
      <div class="auth-card">
        <div class="auth-header">
          <p class="auth-eyebrow">SpecTrace</p>
          <h2>Sign in</h2>
          <p class="auth-subtitle">
            Continue to your sessions and live telemetry.
          </p>
        </div>
        <div class="info-message" *ngIf="sessionExpiredMessage">
          {{ sessionExpiredMessage }}
        </div>
        <div class="info-message" *ngIf="emailConfirmationMessage">
          {{ emailConfirmationMessage }}
        </div>
        <form class="auth-form" (ngSubmit)="onSubmit()">
          <div class="form-group">
            <label for="email">Email</label>
            <input type="email" id="email" [(ngModel)]="email" name="email" required />
          </div>
          <div class="form-group">
            <label for="password">Password</label>
            <input type="password" id="password" [(ngModel)]="password" name="password" required />
          </div>
          <div class="error-message" *ngIf="error">{{ error }}</div>
          <button class="primary-button" type="submit" [disabled]="isLoading || googleLoading">
            {{ isLoading ? 'Logging in...' : 'Login' }}
          </button>
        </form>
        <div class="divider">or</div>
        <button class="google-button" [disabled]="googleLoading || isLoading" (click)="onGoogleLogin()">
          {{ googleLoading ? 'Redirecting...' : 'Continue with Google' }}
        </button>

        <div class="dev-login" *ngIf="showDevMockLogin">
          <div class="dev-login-title">Dev Quick Login</div>
          <div class="dev-login-help" *ngIf="loadingMockUsers">Loading mock users...</div>
          <div class="dev-login-help dev-login-error" *ngIf="!loadingMockUsers && mockUsers.length === 0">
            No mock users available. Start backend with DEV_MOCK_AUTH_ENABLED=true.
          </div>
          <div class="dev-login-buttons" *ngIf="mockUsers.length > 0">
            <button
              type="button"
              class="dev-user-button"
              *ngFor="let user of mockUsers"
              (click)="onDevMockLogin(user)"
              [disabled]="isLoading || googleLoading || devLoginLoadingKey === user.key"
            >
              {{
                devLoginLoadingKey === user.key
                  ? 'Logging in...'
                  : user.name + ' (iR ' + user.iRating + ')'
              }}
            </button>
          </div>
        </div>

        <p class="auth-link">
          Don't have an account? <a routerLink="/register">Register here</a>
        </p>
      </div>
    </div>
  `,
  styles: [`
    .auth-container {
      display: flex;
      justify-content: center;
      align-items: center;
      min-height: 100vh;
      padding: 24px;
      background:
        radial-gradient(circle at 10% 10%, rgba(255, 255, 255, 0.04), transparent 35%),
        radial-gradient(circle at 90% 80%, rgba(255, 255, 255, 0.03), transparent 40%),
        var(--bg-primary);
      color: var(--text-primary);
    }

    .auth-card {
      background: var(--bg-card);
      border: 1px solid var(--border-primary);
      border-radius: var(--radius-lg);
      box-shadow: var(--shadow-lg);
      padding: 28px;
      width: 100%;
      max-width: 440px;
    }

    .auth-header {
      margin-bottom: 20px;
    }

    .auth-eyebrow {
      margin: 0 0 8px;
      color: var(--text-tertiary);
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 0.08em;
      text-transform: uppercase;
    }

    h2 {
      margin: 0;
      color: var(--text-primary);
      font-size: 28px;
      letter-spacing: -0.02em;
    }

    .auth-subtitle {
      margin: 8px 0 0;
      color: var(--text-secondary);
      font-size: 14px;
    }

    .auth-form {
      display: grid;
      gap: 14px;
    }

    .form-group {
      margin: 0;
    }

    label {
      display: block;
      margin-bottom: 8px;
      color: var(--text-secondary);
      font-weight: 500;
      font-size: 12px;
      text-transform: uppercase;
      letter-spacing: 0.06em;
    }

    input {
      width: 100%;
      padding: 12px 14px;
      background: var(--bg-tertiary);
      border: 1px solid var(--border-secondary);
      border-radius: var(--radius-sm);
      color: var(--text-primary);
      font-size: 14px;
      transition: border-color 0.2s ease, background-color 0.2s ease;
    }

    input:focus {
      outline: none;
      border-color: var(--border-primary);
      background: var(--bg-card-hover);
    }

    .primary-button {
      width: 100%;
      padding: 12px 16px;
      background: var(--text-primary);
      color: var(--bg-primary);
      border: 1px solid transparent;
      border-radius: var(--radius-sm);
      font-size: 14px;
      font-weight: 600;
      margin-top: 2px;
      transition: transform 0.15s ease, background-color 0.15s ease, opacity 0.15s ease;
    }

    .primary-button:hover:not(:disabled) {
      transform: translateY(-1px);
      background: var(--text-secondary);
    }

    .primary-button:disabled {
      opacity: 0.45;
      cursor: not-allowed;
      transform: none;
    }

    .divider {
      position: relative;
      text-align: center;
      margin: 18px 0 10px;
      color: var(--text-tertiary);
      font-size: 11px;
      font-weight: 600;
      letter-spacing: 0.08em;
      text-transform: uppercase;
    }

    .divider::before,
    .divider::after {
      content: '';
      position: absolute;
      top: 50%;
      width: calc(50% - 24px);
      height: 1px;
      background: var(--border-secondary);
    }

    .divider::before {
      left: 0;
    }

    .divider::after {
      right: 0;
    }

    .google-button {
      width: 100%;
      padding: 12px 16px;
      background: transparent;
      color: var(--text-primary);
      border: 1px solid var(--border-primary);
      border-radius: var(--radius-sm);
      font-size: 14px;
      font-weight: 600;
      transition: border-color 0.2s ease, background-color 0.2s ease, transform 0.15s ease;
    }

    .google-button:hover:not(:disabled) {
      transform: translateY(-1px);
      background: var(--bg-card-hover);
      border-color: var(--text-tertiary);
    }

    .google-button:disabled {
      opacity: 0.45;
      cursor: not-allowed;
      transform: none;
    }

    .error-message {
      color: #fca5a5;
      margin: -2px 0 0;
      font-size: 13px;
      padding: 10px 12px;
      background: rgba(239, 68, 68, 0.12);
      border: 1px solid rgba(239, 68, 68, 0.35);
      border-radius: var(--radius-sm);
    }

    .info-message {
      background: rgba(245, 158, 11, 0.12);
      color: #fcd34d;
      border: 1px solid rgba(245, 158, 11, 0.36);
      padding: 10px 12px;
      border-radius: var(--radius-sm);
      margin-bottom: 14px;
      font-size: 13px;
      font-weight: 500;
    }

    .auth-link {
      text-align: center;
      margin-top: 14px;
      font-size: 13px;
      color: var(--text-secondary);
    }

    .dev-login {
      margin-top: 14px;
      border-top: 1px solid var(--border-secondary);
      padding-top: 14px;
    }

    .dev-login-title {
      font-size: 12px;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      color: var(--text-tertiary);
      margin-bottom: 8px;
      font-weight: 700;
    }

    .dev-login-help {
      color: var(--text-secondary);
      font-size: 12px;
      margin-bottom: 8px;
    }

    .dev-login-error {
      color: #fca5a5;
    }

    .dev-login-buttons {
      display: grid;
      grid-template-columns: 1fr;
      gap: 8px;
    }

    .dev-user-button {
      width: 100%;
      padding: 10px 12px;
      background: var(--bg-tertiary);
      color: var(--text-primary);
      border: 1px solid var(--border-secondary);
      border-radius: var(--radius-sm);
      font-size: 13px;
      text-align: left;
      transition: border-color 0.15s ease, background-color 0.15s ease, transform 0.15s ease;
    }

    .dev-user-button:hover:not(:disabled) {
      border-color: var(--text-tertiary);
      background: var(--bg-card-hover);
      transform: translateY(-1px);
    }

    .dev-user-button:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      transform: none;
    }

    a {
      color: var(--text-primary);
      text-decoration: none;
    }

    a:hover {
      text-decoration: underline;
    }

    @media (max-width: 560px) {
      .auth-container {
        padding: 16px;
      }

      .auth-card {
        padding: 22px;
      }

      h2 {
        font-size: 24px;
      }
    }
  `]
})
export class LoginComponent implements OnInit {
  email = '';
  password = '';
  error = '';
  isLoading = false;
  googleLoading = false;
  sessionExpiredMessage = '';
  emailConfirmationMessage = '';
  showDevMockLogin = !environment.production;
  mockUsers: DevMockUser[] = [];
  loadingMockUsers = false;
  devLoginLoadingKey: string | null = null;
  private returnUrl = '/';

  constructor(
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private posthogService: PosthogService
  ) {}

  ngOnInit() {
    this.posthogService.capture('login_page_viewed');

    // Check if user was redirected due to session expiration
    this.route.queryParams.subscribe(params => {
      if (params['reason'] === 'session_expired') {
        this.sessionExpiredMessage = 'Your session has expired. Please log in again.';
      }
      if (params['emailConfirmation'] === 'pending') {
        this.emailConfirmationMessage = 'Check your inbox and confirm your email before logging in.';
      }
      // Store the return URL if provided
      if (params['returnUrl']) {
        this.returnUrl = params['returnUrl'];
      }
    });

    if (this.showDevMockLogin) {
      this.loadDevMockUsers();
    }
  }

  onSubmit() {
    if (!this.email || !this.password) {
      this.error = 'Please fill in all fields';
      this.posthogService.capture('login_submit_failed', {
        reason: 'missing_fields',
      });
      return;
    }

    this.isLoading = true;
    this.error = '';
    this.posthogService.capture('login_submit_started');

    this.authService.login({ email: this.email, password: this.password }).subscribe({
      next: () => {
        this.posthogService.capture('login_submit_succeeded');
        // Redirect to the stored return URL or home
        this.router.navigate([this.returnUrl]);
      },
      error: (err) => {
        this.posthogService.capture('login_submit_failed', {
          reason: err?.error?.error || 'request_failed',
        });
        this.error = err.error?.error || 'Login failed';
        this.isLoading = false;
      }
    });
  }

  onGoogleLogin() {
    this.error = '';
    this.googleLoading = true;
    this.posthogService.capture('login_google_started');

    this.authService.loginWithGoogle(this.returnUrl).subscribe({
      error: (err) => {
        this.posthogService.capture('login_google_failed', {
          reason: err?.message || 'request_failed',
        });
        this.error = err?.message || 'Google login failed';
        this.googleLoading = false;
      }
    });
  }

  private loadDevMockUsers() {
    this.loadingMockUsers = true;
    this.authService.getDevMockUsers().subscribe({
      next: (users) => {
        this.mockUsers = users;
        this.loadingMockUsers = false;
      },
      error: () => {
        this.mockUsers = [];
        this.loadingMockUsers = false;
      },
    });
  }

  onDevMockLogin(user: DevMockUser) {
    this.error = '';
    this.devLoginLoadingKey = user.key;
    this.posthogService.capture('login_dev_mock_started', {
      userKey: user.key,
    });
    this.authService.loginAsDevMockUser(user.key).subscribe({
      next: () => {
        this.posthogService.capture('login_dev_mock_succeeded', {
          userKey: user.key,
        });
        this.router.navigate([this.returnUrl]);
      },
      error: (err) => {
        this.posthogService.capture('login_dev_mock_failed', {
          userKey: user.key,
          reason: err?.error?.error || 'request_failed',
        });
        this.error = err.error?.error || 'Dev login failed';
        this.devLoginLoadingKey = null;
      },
      complete: () => {
        this.devLoginLoadingKey = null;
      },
    });
  }
}
